import Cookies from 'js-cookie'
import { handleSessionExpired } from '@/lib/authToken'
import { consumeSseStream } from '@/lib/sse'

const API_BASE = (import.meta.env?.VITE_API_URL as string) || ''

function buildUrl(path: string): string {
  if (API_BASE) return `${API_BASE.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`
  return path.startsWith('/') ? path : `/${path}`
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = Cookies.get('token')
  const headers: Record<string, string> = {}
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  if (token) headers['Authorization'] = `Bearer ${token}`

  const res = await fetch(buildUrl(path), {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  })

  if (!res.ok) {
    if (res.status === 401 && token) {
      handleSessionExpired()
      throw new Error('Session expired')
    }
    const payload = await res.json().catch(() => null)
    const details = Array.isArray(payload?.error?.details) ? payload.error.details : null
    const detailMessage = details?.length
      ? details.map((d: { field?: string; message?: string }) => d.message || d.field).join('; ')
      : null
    let msg = detailMessage || payload?.error?.message || payload?.message || res.statusText || 'Request failed'
    if (typeof msg !== 'string') msg = 'Request failed'
    throw new Error(msg)
  }

  if (res.status === 204) return undefined as T
  return res.json()
}

// ─── Response shapes ──────────────────────────────────────────────────────────

export interface ConversationRecord {
  id: string
  workspaceId: string
  userId: string
  channel: 'web'
  externalThreadId: string | null
  title: string
  status: 'active'
  lastMessageAt: string | null
  createdAt: string
  updatedAt: string
}

export interface ToolCallRecord {
  id: string
  name: string
  arguments: string
  output: unknown
}

export interface MessageRecord {
  id: string
  conversationId: string
  workspaceId: string
  role: 'user' | 'assistant' | 'tool' | 'system'
  content: string
  toolCalls: ToolCallRecord[]
  usage: Record<string, unknown>
  metadata: Record<string, unknown>
  createdAt: string
}

// ─── Conversations ────────────────────────────────────────────────────────────

export function createConversation(
  workspaceId: string,
  opts?: { title?: string; externalThreadId?: string },
): Promise<ConversationRecord> {
  return request('POST', 'api/chat/conversations', {
    workspaceId,
    ...(opts?.title ? { title: opts.title } : {}),
    // Idempotency key — the backend returns an existing conversation for this key
    // instead of creating a duplicate (dashboard "Start chat" per task/ticket).
    ...(opts?.externalThreadId ? { externalThreadId: opts.externalThreadId } : {}),
  })
}

export function listConversations(workspaceId: string): Promise<ConversationRecord[]> {
  return request('GET', `api/chat/conversations?workspaceId=${encodeURIComponent(workspaceId)}`)
}

export function getConversation(id: string): Promise<ConversationRecord> {
  return request('GET', `api/chat/conversations/${id}`)
}

export function updateConversation(id: string, title: string): Promise<ConversationRecord> {
  return request('PATCH', `api/chat/conversations/${id}`, { title })
}

export function deleteConversation(id: string): Promise<void> {
  return request('DELETE', `api/chat/conversations/${id}`)
}

// ─── Messages ─────────────────────────────────────────────────────────────────

export function createMessage(conversationId: string, content: string): Promise<MessageRecord> {
  return request('POST', `api/chat/conversations/${conversationId}/messages`, { content })
}

export function listMessages(conversationId: string): Promise<MessageRecord[]> {
  return request('GET', `api/chat/conversations/${conversationId}/messages`)
}

// ─── Credit status ────────────────────────────────────────────────────────────

export interface MyCreditStatus {
  allocation: number | null
  used: number
  remaining: number | null
  percentUsed: number | null
}

export function getMyCreditStatus(workspaceId: string): Promise<MyCreditStatus> {
  return request('GET', `api/workspaces/${workspaceId}/credits/me`)
}

// ─── SSE Streaming ────────────────────────────────────────────────────────────

export interface PendingApproval {
  toolCallId: string
  toolName: string
  arguments: string
}

// The approval queue (GET /api/chat/approvals) — one row per conversation
// currently interrupted waiting on a sensitive-action decision. This is a
// discovery index over a live LangGraph interrupt, not a second store: the
// checkpoint is still what `resolveApproval` (dashboardApi.ts) actually acts
// on when you approve/reject from here.
export interface PendingApprovalSummary {
  id: string
  conversationId: string
  workspaceId: string
  userId: string
  tools: { toolCallId: string; name: string; arguments: Record<string, unknown> }[]
  requestedAt: string
}

export function listPendingApprovals(workspaceId: string): Promise<{ approvals: PendingApprovalSummary[] }> {
  return request('GET', `api/chat/approvals?workspaceId=${workspaceId}`)
}

export type SseEvent =
  | { type: 'message_start'; conversationId: string }
  | { type: 'token'; content: string }
  | { type: 'status'; status: string }
  | { type: 'tool_call'; id: string; name: string; arguments: string }
  | { type: 'tool_start'; id: string; name: string; arguments?: string }
  | { type: 'tool_result'; toolCallId: string; name: string; output: unknown }
  | { type: 'message_complete'; messageId: string }
  | { type: 'conversation_updated'; title: string }
  | { type: 'approval_required'; tools: PendingApproval[] }
  | { type: 'error'; message: string }
  | { type: 'usage_warning'; percentUsed: number; remaining: number }
  | { type: 'usage_blocked' }
  | { type: 'profile_incomplete_warning' }

async function consumeStream(
  res: Response,
  onEvent: (event: SseEvent) => void,
): Promise<void> {
  await consumeSseStream(res, (eventType, data) => {
    const parsed = data as Record<string, any>
    switch (eventType) {
      case 'message_start': onEvent({ type: 'message_start', conversationId: parsed.conversationId }); break
      case 'token': onEvent({ type: 'token', content: parsed.content }); break
      case 'status': onEvent({ type: 'status', status: parsed.status }); break
      case 'tool_call': onEvent({ type: 'tool_call', id: parsed.id, name: parsed.name, arguments: parsed.arguments }); break
      case 'tool_start': onEvent({ type: 'tool_start', id: parsed.id, name: parsed.name, arguments: parsed.arguments }); break
      case 'tool_result': onEvent({ type: 'tool_result', toolCallId: parsed.toolCallId, name: parsed.name, output: parsed.output }); break
      case 'message_complete': onEvent({ type: 'message_complete', messageId: parsed.messageId }); break
      case 'conversation_updated': onEvent({ type: 'conversation_updated', title: parsed.title }); break
      case 'approval_required': onEvent({ type: 'approval_required', tools: parsed.tools }); break
      case 'error': onEvent({ type: 'error', message: parsed.message }); break
      case 'usage_warning': onEvent({ type: 'usage_warning', percentUsed: parsed.percentUsed, remaining: parsed.remaining }); break
      case 'usage_blocked': onEvent({ type: 'usage_blocked' }); break
      case 'profile_incomplete_warning': onEvent({ type: 'profile_incomplete_warning' }); break
    }
  })
}

export async function streamConversation(
  conversationId: string,
  onEvent: (event: SseEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const token = Cookies.get('token')
  const headers: Record<string, string> = { Accept: 'text/event-stream' }
  if (token) headers['Authorization'] = `Bearer ${token}`

  const res = await fetch(buildUrl(`api/chat/conversations/${conversationId}/stream`), {
    method: 'GET',
    headers,
    ...(signal ? { signal } : {}),
  })

  if (!res.ok) {
    if (res.status === 401 && token) {
      handleSessionExpired()
      throw new Error('Session expired')
    }
    const payload = await res.json().catch(() => null)
    let msg = payload?.error?.message || payload?.message || res.statusText || 'Stream failed'
    if (typeof msg !== 'string') msg = 'Stream failed'
    throw new Error(msg)
  }

  await consumeStream(res, onEvent)
}

export async function submitApproval(
  conversationId: string,
  decision: { action: 'approved' | 'rejected' | 'edited'; editedToolCalls?: any[] },
  onEvent: (event: SseEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const token = Cookies.get('token')
  const headers: Record<string, string> = {
    Accept: 'text/event-stream',
    'Content-Type': 'application/json',
  }
  if (token) headers['Authorization'] = `Bearer ${token}`

  const res = await fetch(buildUrl(`api/chat/conversations/${conversationId}/approve`), {
    method: 'POST',
    headers,
    body: JSON.stringify(decision),
    ...(signal ? { signal } : {}),
  })

  if (!res.ok) {
    if (res.status === 401 && token) {
      handleSessionExpired()
      throw new Error('Session expired')
    }
    const payload = await res.json().catch(() => null)
    let msg = payload?.error?.message || payload?.message || res.statusText || 'Approval failed'
    if (typeof msg !== 'string') msg = 'Approval failed'
    throw new Error(msg)
  }

  await consumeStream(res, onEvent)
}
